import React, { useState } from 'react';
import {
  BrowserRouter,
  Switch,
  Route,
  RouteProps,
  Redirect,
} from 'react-router-dom';
import {
  AuthenticationProvider,
  useAuthentication,
} from './context/AuthenticationProvider';
import { GifteesProvider } from './context/GifteesProvider';
import { Header } from './layouts/Header';
import { ShoppingCartProvider } from './context/ShoppingCartProvider';
import { routes } from './routes';
import { Footer } from './layouts/Footer';
import { useQuery } from 'react-query';
import { WebsiteSettings } from 'shared/build/types';
import { customFetch } from './util/functions';
import { ToastContainer } from 'react-toastify';
import { FullscreenSpinner } from './components/FullscreenSpinner';
import { ParentsProvider } from './context/ParentsProvider';

const App = () => {
  return (
    <AuthenticationProvider>
      <Providers>
        <AppContent />
      </Providers>
    </AuthenticationProvider>
  );
};

const AppContent = () => {
  return (
    <>
      <div>
        <Switch>
          <ProtectedRoute
            path={routes
              .filter((route) => route.showHeader)
              .flatMap((route) => route.path)}
          >
            <Header />
          </ProtectedRoute>
        </Switch>
      </div>
      <div>
        <ToastContainer />
        <Switch>
          {routes.map((route, i) =>
            route.isProtected ? (
              <ProtectedRoute
                key={i}
                path={route.path}
                children={route.component()}
              />
            ) : (
              <Route key={i} path={route.path} children={route.component()} />
            )
          )}
        </Switch>
      </div>
      <div>
        <Switch>
          <Route
            path={routes
              .filter((route) => route.showFooter)
              .flatMap((route) => route.path)}
          >
            <Footer />
          </Route>
        </Switch>
      </div>
    </>
  );
};

const ProtectedRoute = (args: RouteProps) => {
  const { user } = useAuthentication();
  const { children, ...rest } = args;

  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (!user) {
          return (
            <Redirect
              to={{
                pathname: `/login?returnTo=${location.pathname}`,
                state: { from: location },
              }}
            />
          );
        }
        return children;
      }}
    />
  );
};

const Providers = ({ children }: { children: React.ReactNode }) => {
  const {
    user,
    initializing: initializingAuthentication,
  } = useAuthentication();
  const [initializingSettings, setInitializingSettings] = useState<boolean>(
    true
  );

  const { data: settings, refetch } = useQuery<WebsiteSettings, string>(
    'website-settings-get',
    async (): Promise<WebsiteSettings> => {
      const response = await customFetch(`/api/website/settings`);
      if (response.ok) {
        setInitializingSettings(false);
        return response.json();
      }
      throw new Error('exploded');
    },
    {
      cacheTime: Infinity,
      staleTime: 1000 * 60 * 60,
      enabled: !initializingAuthentication && user,
    }
  );

  if (initializingAuthentication || (user && initializingSettings)) {
    return <FullscreenSpinner show={true} />;
  }

  return (
    <GifteesProvider
      gifteesBasicProperties={settings?.giftees || []}
      shouldGuessGender={settings?.shouldGuessGender || false}
      selectedGender={user?.selectedGender}
      refresh={() => refetch()}
    >
      <ShoppingCartProvider initialShoppingCart={settings?.shoppingCart}>
        <ParentsProvider initialParents={settings?.parents || []}>
          <BrowserRouter>{children}</BrowserRouter>
        </ParentsProvider>
      </ShoppingCartProvider>
    </GifteesProvider>
  );
};

export default App;
